import React, { useEffect, useRef, useState } from 'react';
import {
  DropDownContainer,
  DropDownHook,
  Grid,
  Label,
  Para,
  TextButton,
  Theme,
} from 'uie/components';
import { FormButton } from 'library/atoms/button/FormButton';
import { DateRangePicker, createStaticRanges } from 'react-date-range';
import { Box, Divider, HStack } from '@chakra-ui/react';
import SelectTimeZone from 'components/EventDateRangePicker/timezone';
import moment from 'moment';
import { DateRangePickerContainer } from './style';

const { theme } = Theme;

interface IProps {
  selectedDates: Date[];
  minDate?: Date;
  maxDate?: Date;
  onUpdateRange: (date: Date, dateString: string) => void;
  dateString: string;
  onApply: (startDate: Date, endDate: Date, timeZone: string) => void;
  timezone: string;
  calculateMaxDate?: boolean;
}

const DateTimePickerV2: React.FC<IProps> = ({
  onApply,
  selectedDates,
  timezone,
  maxDate,
  minDate,
  calculateMaxDate,
}) => {
  const [mountDropdown, setMountDropdown] = useState(false);
  const [isFirstInitalized, setIsFirstInitalized] = useState(true);

  const [selectedRange, setSelectedRange] = useState([
    {
      startDate: selectedDates[0],
      endDate: selectedDates[1],
      key: 'selection',
    },
  ]);

  const [selctedDate, setSelectedDate] = useState(undefined);
  const [selectedTimeZone, setSelectedTimeZone] = useState(timezone);

  useEffect(() => {
    if (timezone) {
      setSelectedTimeZone(timezone);
    }
  }, [timezone]);

  useEffect(() => {
    setSelectedRange([
      {
        startDate: selectedDates[0],
        endDate: selectedDates[1],
        key: 'selection',
      },
    ]);
  }, [selectedDates]);

  setTimeout(() => {
    setMountDropdown(true);
  }, 200);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dateRangeRef = useRef<any>();

  const handleApplyRange = () => {
    const { startDate, endDate } = selectedRange[0];
    onApply(startDate, endDate, selectedTimeZone);
    closeDropdown(); //todo
  };

  const onCancel = () => {
    closeDropdown(); //todo
  };

  const closeDropdown = () => {
    if (dropdownRef.current) {
      dropdownRef.current?.focus();
      dropdownRef.current?.blur();
    }
  };

  useEffect(() => {
    // Scroll to bottom
    if (dateRangeRef.current && isFirstInitalized) {
      try {
        // Scrollable Element
        const calendar = dateRangeRef.current.dateRange.calendar.list.scrollParent;
        calendar.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } catch (error) {
        console.log(error);
      }
    }
  }, [isFirstInitalized]);

  const customStaticRanges = createStaticRanges([
    {
      label: 'Today',
      range: () => ({
        startDate: moment(new Date()).startOf('day').toDate(),
        endDate: moment(new Date()).endOf('day').toDate(),
      }),
    },
    {
      label: 'Yesterday',
      range: () => {
        return {
          startDate: moment(new Date()).subtract(1, 'day').startOf('day').toDate(),
          endDate: moment(new Date()).subtract(1, 'day').endOf('day').toDate(),
        };
      },
    },
    {
      label: 'This Week',
      range: () => {
        const now = moment(new Date()).endOf('day').toDate();
        const startOfWeek = moment(new Date()).startOf('week').toDate();
        return {
          startDate: startOfWeek,
          endDate: now,
        };
      },
    },
    {
      label: 'Last Week',
      range: () => {
        const startOfLastWeek = moment().subtract(1, 'week').startOf('week').toDate();
        const endOfLastWeek = moment().subtract(1, 'week').endOf('week').toDate();
        return {
          startDate: startOfLastWeek,
          endDate: endOfLastWeek,
        };
      },
    },
    {
      label: 'This Month',
      range: () => {
        const now = moment(new Date()).endOf('day').toDate();
        const startOfMonth = moment(new Date()).startOf('month').toDate();
        return {
          startDate: startOfMonth,
          endDate: now,
        };
      },
    },
    {
      label: 'Last Month',
      range: () => {
        const startOfLastMonth = moment(new Date()).subtract(1, 'month').startOf('month').toDate();
        const endOfLastMonth = moment(new Date()).subtract(1, 'month').endOf('month').toDate();
        return {
          startDate: startOfLastMonth,
          endDate: endOfLastMonth,
        };
      },
    },
  ]);

  const startFormatDate = moment(selectedRange[0].startDate).format('DD MMM YYYY');
  const endFormatDate = moment(selectedRange[0].endDate).format('DD MMM YYYY');

  return (
    <Grid alignItems="center">
      <DropDownHook
        style={{ width: '100%' }}
        ref={dropdownRef}
        onClick={() => setIsFirstInitalized(true)}
      >
        <FormButton
          title={`${startFormatDate} - ${endFormatDate} ${selectedTimeZone}`}
          variant="secondary"
          pt={'7px'}
        />
        <DropDownContainer offset={'-650px'} style={{ padding: '0' }}>
          {mountDropdown && (
            <Grid type="column">
              <HStack padding={5} justifyContent="center" gap={2}>
                <Label>Select Timezone</Label>
                <Box minWidth={422}>
                  <SelectTimeZone
                    timezone={selectedTimeZone}
                    handleChange={tz => {
                      setSelectedTimeZone(tz);
                    }}
                    hideDefaultTimezone={true}
                  />
                </Box>
              </HStack>
              <Divider height="1px" flex={1} />

              <DateRangePickerContainer>
                <DateRangePicker
                  onChange={(item: any) => {
                    if (calculateMaxDate) {
                      const startDate = item.selection.startDate;
                      let endDate = item.selection.endDate;

                      const maxAllowedDate = moment(startDate).add(30, 'days').toDate();

                      if (moment(endDate).isAfter(maxAllowedDate)) {
                        endDate = maxAllowedDate;
                      }

                      const updatedRange = [
                        {
                          startDate,
                          endDate,
                          key: 'selection',
                        },
                      ];

                      setSelectedRange(updatedRange);
                      setSelectedDate(updatedRange as any);
                    } else {
                      setSelectedDate([item.selection] as any);
                      setSelectedRange([item.selection]);
                    }
                  }}
                  showDateDisplay={false}
                  moveRangeOnFirstSelection={false}
                  months={2}
                  ranges={selectedRange}
                  direction="horizontal"
                  preventSnapRefocus={true}
                  calendarFocus="forwards"
                  minDate={minDate}
                  maxDate={maxDate}
                  staticRanges={customStaticRanges}
                  inputRanges={[]}
                  retainEndDateOnFirstSelection
                />
              </DateRangePickerContainer>
              <Grid justifyContent="flex-end">
                <TextButton buttonType="ghost" style={{ margin: '12px' }} onClick={onCancel}>
                  <Para fontSize={14}>Cancel</Para>
                </TextButton>
                <TextButton
                  onClick={handleApplyRange}
                  style={{ margin: '12px', float: 'left', fontSize: '14px' }}
                  disabled={!selctedDate && !selectedTimeZone}
                >
                  <Para fontSize={14} color={theme.shades.white}>
                    Apply
                  </Para>
                </TextButton>
              </Grid>
            </Grid>
          )}
        </DropDownContainer>
      </DropDownHook>
    </Grid>
  );
};

export default DateTimePickerV2;
